import type { SalesOrderPrintTemplate } from '@/lib/services/snapdragon/snapdragon.types'

/** How a template renders: the order summary page or a pre-printed super ticket form. */
export type SalesOrderPrintFormat = 'order_summary' | 'super_ticket'

export interface SalesOrderPrintTemplateOption {
  value: SalesOrderPrintTemplate
  label: string
  /** Where a shop can buy the matching pre-printed paper stock. */
  purchaseUrl?: string
  format: SalesOrderPrintFormat
  supportsPreprintedBranding: boolean
}

export const salesOrderPrintTemplates = Object.freeze({
  OrderSummary: 'order_summary',
  JhcFb7373SuperTicket: 'jhc_fb7373_super_ticket',
  FloranextLetterSuperTicket: 'floranext_letter_super_ticket',
  JhcMercuryXOrderEntrySuperTicket: 'jhc_mercury_x_order_entry_super_ticket',
  JhcFb2158TelefloraDaisyEFloristSuperTicket: 'jhc_fb2158_teleflora_daisy_eflorist_super_ticket',
  JhcFb1780BloomnetCustomSuperTicket: 'jhc_fb1780_bloomnet_custom_super_ticket',
  JhcBc0164LaserEnclosureCard: 'jhc_bc0164_laser_enclosure_card',
  EnclosureCardOnlyLetter: 'enclosure_card_only_letter',
} satisfies Record<string, SalesOrderPrintTemplate>)

export const salesOrderPrintTemplateOptions: readonly SalesOrderPrintTemplateOption[] =
  Object.freeze([
    {
      value: salesOrderPrintTemplates.OrderSummary,
      label: 'Order Summary',
      format: 'order_summary',
      supportsPreprintedBranding: false,
    },
    {
      value: salesOrderPrintTemplates.JhcFb7373SuperTicket,
      label: 'Teleflora Dove/RTI Ticket (Legal - 8.5 x 14)',
      purchaseUrl:
        'https://www.jhc.com/Index.asp?Action=Products&Category=8&SubCategory=23&Sub2Category=235&Sub3Category=40',
      format: 'super_ticket',
      supportsPreprintedBranding: true,
    },
    {
      value: salesOrderPrintTemplates.FloranextLetterSuperTicket,
      label: 'FloraNext Super Ticket (Letter - 8.5 x 11)',
      purchaseUrl:
        'https://www.jhc.com/Index.asp?Action=Products&Category=8&SubCategory=23&Sub2Category=251',
      format: 'super_ticket',
      supportsPreprintedBranding: true,
    },
    {
      value: salesOrderPrintTemplates.JhcMercuryXOrderEntrySuperTicket,
      label: 'FTD Mercury X Order Entry (Legal - 8.5 x 14, Portrait)',
      purchaseUrl:
        'https://www.jhc.com/Index.asp?Action=Products&Category=8&SubCategory=23&Sub2Category=236&Sub3Category=38',
      format: 'super_ticket',
      supportsPreprintedBranding: true,
    },
    {
      value: salesOrderPrintTemplates.JhcFb2158TelefloraDaisyEFloristSuperTicket,
      label: 'Teleflora Daisy/E-Florist Ticket (Letter - 8.5 x 11, Portrait)',
      purchaseUrl:
        'https://www.jhc.com/Index.asp?Action=Products&Category=8&SubCategory=23&Sub2Category=276',
      format: 'super_ticket',
      supportsPreprintedBranding: true,
    },
    {
      value: salesOrderPrintTemplates.JhcFb1780BloomnetCustomSuperTicket,
      label: 'BloomNet Custom Superticket (Letter - 8.5 x 11, Portrait)',
      purchaseUrl: 'https://www.jhc.com/Index.asp?Action=Display&PartNumber=FB1780&Rank=1',
      format: 'super_ticket',
      supportsPreprintedBranding: true,
    },
    {
      value: salesOrderPrintTemplates.JhcBc0164LaserEnclosureCard,
      label: 'Laser Enclosure Card & Envelope (#10 Envelope - 4.125 x 9.5)',
      purchaseUrl: 'https://www.jhc.com/Index.asp?Action=Display&PartNumber=BC0164&Rank=2',
      format: 'super_ticket',
      supportsPreprintedBranding: true,
    },
    {
      value: salesOrderPrintTemplates.EnclosureCardOnlyLetter,
      label: 'Enclosure Card Only (Letter - 8.5 x 11, Landscape)',
      format: 'super_ticket',
      supportsPreprintedBranding: false,
    },
  ])

export const salesOrderPrintTemplateValues = Object.freeze(
  salesOrderPrintTemplateOptions.map(({ value }) => value)
)

export const salesOrderPrintTemplateOptionsByValue: Readonly<
  Partial<Record<string, SalesOrderPrintTemplateOption>>
> = Object.freeze(
  Object.fromEntries(salesOrderPrintTemplateOptions.map((option) => [option.value, option]))
)
